import { useState } from 'react'
import { ArrowUpRight, X, Check, Briefcase, AlertTriangle } from 'lucide-react'
import { useMe } from '@/features/auth/hooks'
import { cn } from '@/lib/cn'
import {
  useBulkAssign,
  useBulkClose,
  useBulkEscalate,
} from '../../hooks/useAlertActions'
import { AnalystPicker } from './AnalystPicker'
import { AlertLinker } from '@/features/cases/components/AlertLinker'
import type { AlertSummary, AnalystSummary } from '../../types'

type ConfirmAction =
  | { kind: 'assign'; user_id: string; assignee_label: string }
  | { kind: 'escalate' }
  | { kind: 'close' }

interface Props {
  selectedIds: readonly string[]
  onClear: () => void
  /**
   * Full alert summaries on the queue page so AlertLinker can
   * derive title / summary / priority / queue defaults from the
   * selected alerts without an extra fetch. Optional — falls back
   * to empty defaults if not provided.
   */
  alerts?: ReadonlyArray<AlertSummary>
}

/**
 * AF-126 — Bulk actions bar.
 *
 * Sticky pill at the bottom-center; inverted color so it reads as decisive.
 * Click → bar morphs into a confirm step in place (no modal context-switch),
 * except "Add to case" which opens the LinkToCaseDialog (case-picker UX).
 */
export function BulkActionsBar({ selectedIds, onClear, alerts }: Props) {
  const selectedSet = new Set(selectedIds)
  const prefillAlerts = (alerts ?? []).filter((a) => selectedSet.has(a.id))
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const { data: me } = useMe()

  const assign = useBulkAssign()
  const close = useBulkClose()
  const escalate = useBulkEscalate()

  if (selectedIds.length === 0) return null

  const onConfirm = async (): Promise<void> => {
    if (!confirm) return
    const ids = [...selectedIds]
    if (confirm.kind === 'assign') {
      await assign.mutateAsync({ alert_ids: ids, user_id: confirm.user_id })
    } else if (confirm.kind === 'close') {
      await close.mutateAsync({ alert_ids: ids, resolution: 'false_positive' })
    } else if (confirm.kind === 'escalate') {
      await escalate.mutateAsync({
        alert_ids: ids,
        reason: 'Bulk escalation from queue',
      })
    }
    setConfirm(null)
    onClear()
  }

  const onPickAnalyst = (a: AnalystSummary | { user_id: 'me' }) => {
    if (a.user_id === 'me') {
      if (!me) return
      setConfirm({ kind: 'assign', user_id: me.id, assignee_label: 'me' })
    } else {
      const sum = a as AnalystSummary
      setConfirm({ kind: 'assign', user_id: sum.user_id, assignee_label: sum.display_name })
    }
  }

  return (
    <>
      <div
        role="region"
        aria-label="Bulk actions"
        className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
      >
        <div className="flex items-center gap-2 rounded-full bg-fg px-3 py-2 text-bg shadow-2xl">
          <span className="flex items-center gap-1 px-1 text-sm">
            <span className="font-mono tabular-nums">{selectedIds.length}</span>
            <span className="text-bg/70">selected</span>
          </span>
          <Separator />

          {confirm ? (
            <>
              <span className="px-2 text-sm">{confirmCopy(confirm, selectedIds.length)}</span>
              <button
                type="button"
                onClick={() => void onConfirm()}
                className="flex items-center gap-1 rounded-full bg-bg px-3 py-1 text-sm text-fg hover:bg-bg/90"
              >
                <Check size={12} />
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="rounded-full px-2 py-1 text-sm text-bg/70 hover:text-bg"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <AnalystPicker
                triggerLabel="Assign…"
                inverted
                onPick={onPickAnalyst}
              />
              <BarButton
                icon={<ArrowUpRight size={12} />}
                label="Escalate"
                onClick={() => setConfirm({ kind: 'escalate' })}
              />
              <BarButton
                icon={<AlertTriangle size={12} />}
                label="Close as FP"
                onClick={() => setConfirm({ kind: 'close' })}
              />
              <BarButton
                icon={<Briefcase size={12} />}
                label="Add to case"
                onClick={() => setLinkOpen(true)}
              />
              <Separator />
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear selection"
                className="rounded-full p-1 text-bg/70 hover:bg-bg/10 hover:text-bg"
              >
                <X size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      <AlertLinker
        open={linkOpen}
        onOpenChange={setLinkOpen}
        alertIds={selectedIds}
        prefillAlerts={prefillAlerts.length > 0 ? prefillAlerts : undefined}
        onLinked={() => onClear()}
      />
    </>
  )
}

function BarButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1 rounded-full px-3 py-1 text-sm transition-colors',
        'text-bg hover:bg-bg/10',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

function Separator() {
  return <span className="h-4 w-px bg-bg/20" aria-hidden />
}

function confirmCopy(action: ConfirmAction, count: number): string {
  const plural = count === 1 ? 'alert' : 'alerts'
  switch (action.kind) {
    case 'assign':
      return `Assign ${count} ${plural} to ${action.assignee_label}?`
    case 'escalate':
      return `Escalate ${count} ${plural}?`
    case 'close':
      return `Close ${count} ${plural} as false positive?`
  }
}
